/**
 * Componente para exibição de notificação de chamada perdida
 * 
 * Design baseado na referência do WhatsApp:
 * - Ícone de telefone vermelho (indicando perdida)
 * - Título em negrito: "Ligação de voz/vídeo perdida"
 * - Subtexto: "Toque para retornar" + horário
 * - Fundo com borda arredondada
 */

import { Phone, Video, PhoneIncoming, PhoneOutgoing, PhoneMissed } from 'lucide-react';
import { cn } from '@/lib/utils';

// Tipagem para props do componente
export interface MissedCallMessageBubbleProps {
  /** Tipo de chamada: voz ou vídeo */
  callType: 'voice' | 'video';
  /** Status da chamada: perdida, recebida ou rejeitada */
  callStatus: 'missed' | 'received' | 'rejected';
  /** Direção da chamada: entrada (inbound) ou saída (outbound) */
  direction: 'inbound' | 'outbound';
  /** Duração da chamada em segundos (se disponível - para chamadas atendidas) */
  duration?: number;
  /** Horário formatado da chamada (ex: "17:00") */
  time: string;
}

/**
 * Formata duração em segundos para formato legível (mm:ss ou hh:mm:ss)
 */
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Retorna o ícone apropriado baseado no tipo e status da chamada
 */
const getCallIcon = (
  callType: 'voice' | 'video',
  callStatus: 'missed' | 'received' | 'rejected',
  direction: 'inbound' | 'outbound'
) => {
  // Chamada perdida/rejeitada - sempre ícone de telefone com seta vermelha
  if (callStatus === 'missed' || callStatus === 'rejected') {
    return callType === 'video' ? Video : PhoneMissed;
  }
  
  // Chamada atendida
  if (direction === 'inbound') {
    return callType === 'video' ? Video : PhoneIncoming;
  }
  
  return callType === 'video' ? Video : PhoneOutgoing;
};

/**
 * Retorna a cor do ícone baseado no status da chamada
 */
const getIconColorClass = (callStatus: 'missed' | 'received' | 'rejected'): string => {
  if (callStatus === 'missed' || callStatus === 'rejected') {
    return 'text-destructive';
  }
  return 'text-primary';
};

/**
 * Retorna o título da notificação baseado no tipo e status
 */
const getCallTitle = (
  callType: 'voice' | 'video',
  callStatus: 'missed' | 'received' | 'rejected'
): string => {
  const type = callType === 'video' ? 'Ligação de vídeo' : 'Ligação de voz';
  
  switch (callStatus) {
    case 'missed':
      return `${type} perdida`;
    case 'rejected':
      return `${type} recusada`;
    case 'received':
      return type;
    default:
      return type;
  }
};

/**
 * Retorna o subtexto da notificação
 */
const getCallSubtext = (
  callStatus: 'missed' | 'received' | 'rejected',
  duration?: number
): string => {
  if (callStatus === 'received' && duration !== undefined) {
    return formatDuration(duration);
  }
  
  if (callStatus === 'missed') {
    return 'Toque para retornar';
  }
  
  if (callStatus === 'rejected') {
    return 'Chamada recusada';
  }
  
  return '';
};

export function MissedCallMessageBubble({
  callType,
  callStatus,
  direction,
  duration,
  time,
}: MissedCallMessageBubbleProps) {
  const Icon = getCallIcon(callType, callStatus, direction);
  const iconColorClass = getIconColorClass(callStatus);
  const title = getCallTitle(callType, callStatus);
  const subtext = getCallSubtext(callStatus, duration);
  
  // Verifica se é chamada perdida/rejeitada para aplicar estilo especial
  const isMissed = callStatus === 'missed' || callStatus === 'rejected';
  
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg',
        'bg-muted/50 border border-border/50',
        'min-w-[200px] max-w-[280px]'
      )}
    >
      {/* Ícone da chamada */}
      <div
        className={cn(
          'flex items-center justify-center',
          'w-10 h-10 rounded-full',
          isMissed ? 'bg-destructive/10' : 'bg-primary/10'
        )}
      >
        <Icon
          className={cn('h-5 w-5', iconColorClass)}
        />
      </div>
      
      {/* Conteúdo textual */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Título em negrito */}
        <span
          className={cn(
            'text-sm font-medium truncate',
            isMissed ? 'text-destructive' : 'text-foreground'
          )}
        >
          {title}
        </span>
        
        {/* Subtexto + horário */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {subtext && (
            <>
              <span className="truncate">{subtext}</span>
              <span>•</span>
            </>
          )}
          <span className="shrink-0">{time}</span>
        </div>
      </div>
    </div>
  );
}
